import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { FullCaseDetails } from '../../../../interfaces/cliente.types'; // Asumiendo que Audiencia está en cliente.types o en su propio archivo
import { Audiencia } from '../../../../interfaces/audiencia.types';
import styles from './AgendaCard.module.css';
import { CalendarClock, Video, Building, PlusCircle } from 'lucide-react';

interface CasoAgendaCardProps {
    audiencias: Audiencia[];
    onAddAudiencia: () => void;
}

export const CasoAgendaCard: React.FC<CasoAgendaCardProps> = ({ audiencias, onAddAudiencia }) => {
    
    const contentVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
    };

    const { proximasAudiencias, historialAudiencias } = useMemo(() => {
        const ahora = new Date();
        const proximas = audiencias.filter(a => new Date(a.fecha_hora) >= ahora).sort((a, b) => new Date(a.fecha_hora).getTime() - new Date(b.fecha_hora).getTime());
        const historial = audiencias.filter(a => new Date(a.fecha_hora) < ahora).sort((a, b) => new Date(b.fecha_hora).getTime() - new Date(a.fecha_hora).getTime());
        return { proximasAudiencias: proximas, historialAudiencias: historial };
    }, [audiencias]);
    
    const renderAudienciaItem = (audiencia: Audiencia) => (
        <motion.div key={audiencia.id} className={`${styles.agendaItem} ${styles[`status${audiencia.estado}`]}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} layout>
            <div className={styles.dateInfo}>
                <span className={styles.day}>{new Date(audiencia.fecha_hora).toLocaleDateString('es-PE', { day: '2-digit' })}</span>
                <span className={styles.month}>{new Date(audiencia.fecha_hora).toLocaleDateString('es-PE', { month: 'short' }).replace('.', '')}</span>
            </div>
            <div className={styles.agendaDetails}>
                <span className={styles.agendaDescription}>{audiencia.descripcion}</span>
                <div className={styles.locationInfo}>
                    {audiencia.lugar_o_enlace.startsWith('http') ? <Video size={16} /> : <Building size={16} />}
                    <span>{audiencia.lugar_o_enlace}</span>
                </div>
            </div>
            <div className={styles.timeInfo}>
                <span className={styles.statusBadge}>{audiencia.estado}</span>
                <span className={styles.timeText}>{new Date(audiencia.fecha_hora).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
            </div>
        </motion.div>
    );

    return (
        <motion.div className={styles.card} variants={contentVariants} initial="hidden" animate="visible" exit="hidden">
            <div className={styles.listHeader}>
                <h3 className={styles.cardTitle}>Agenda de Diligencias</h3>
                <button className={styles.addButton} onClick={onAddAudiencia}>
                    <PlusCircle size={18}/>
                    <span>Agendar Diligencia</span>
                </button>
            </div>
            <div className={styles.section}>
                <h4 className={styles.sectionTitle}>Próximas Diligencias</h4>
                <div className={styles.agendaList}>
                    {proximasAudiencias.length > 0 ? proximasAudiencias.map(renderAudienciaItem) : <p className={styles.emptyList}>No hay diligencias futuras programadas.</p>}
                </div>
            </div>
            <div className={styles.section}>
                <h4 className={styles.sectionTitle}>Historial de Diligencias</h4>
                <div className={styles.agendaList}>
                     {historialAudiencias.length > 0 ? historialAudiencias.map(renderAudienciaItem) : <p className={styles.emptyList}>No hay diligencias pasadas en el registro.</p>}
                </div>
            </div>
        </motion.div>
    );
};